import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Sparkles, Users, Tag, Truck, Quote, Star, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Seo from "@/components/Seo";
import { onImgError } from "@/lib/image";
import { useCategories } from "@/lib/categories";

const HERO = "https://images.unsplash.com/photo-1628242345770-ec44a4f72017";
const HERO_VIDEO = "/hero-bg.mp4";
const CLONE_IMG = "/api/media/darkgrowers/products/cab513d878164a2396e741397e97be88.jpg";
const SEED_IMG = "/api/media/darkgrowers/products/d196604b17864485aa6039d1d8e68468.jpg";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [bigNan, setBigNan] = useState([]);
  const [drop, setDrop] = useState({ theme_label: "", products: [] });
  const categories = useCategories();
  const [clones, setClones] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [reviews, setReviews] = useState([]);
  const reviewsRef = useRef(null);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then(({ data }) => setFeatured(data.slice(0, 8)));
    api.get("/products", { params: { category: "seeds" } }).then(({ data }) =>
      setBigNan(data.filter((p) => (p.lineage || "").toLowerCase().includes("big nan")))
    );
    api.get("/weekly-drop").then(({ data }) => setDrop(data)).catch(() => {});
    api.get("/clones").then(({ data }) => setClones(data)).catch(() => {});
    api.get("/reviews").then(({ data }) => setReviews(data || [])).catch(() => {});
  }, []);

  const scrollReviews = (dir) => {
    const el = reviewsRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.85), behavior: "smooth" });
  };

  // Sync from the URL only on mount (e.g. /#gas) and on real back/forward (popstate).
  // The pill click is the writer; there is no hashchange listener to fight it.
  useEffect(() => {
    console.log("[DG-PILL] category browser build: INSTRUMENTED-v4 loaded");
    const syncFromUrl = () => {
      const h = window.location.hash.replace(/^#/, "").trim().toLowerCase();
      setActiveCat(h || "all");
    };
    syncFromUrl();
    window.addEventListener("popstate", syncFromUrl);
    return () => window.removeEventListener("popstate", syncFromUrl);
  }, []);

  const selectCat = (slug) => {
    console.log("[DG-PILL] onClick handler fired ->", slug, "| hash before:", window.location.hash);
    setActiveCat(slug); // instant visual feedback / single source of truth
    const base = window.location.pathname + window.location.search;
    // pushState adds a history entry (back/forward works) without firing popstate,
    // so nothing can revert the click.
    window.history.pushState(null, "", slug === "all" ? base : `${base}#${slug}`);
    console.log("[DG-PILL] hash after:", window.location.hash);
  };

  // If the hash points at a category that no longer exists, fall back to All.
  const validCat = activeCat === "all" || categories.some((c) => c.slug === activeCat) ? activeCat : "all";
  const soldOut = (p) => ((p.availability || "preorder") !== "preorder" && (p.stock ?? 0) <= 0 ? 1 : 0);
  const filteredClones = clones
    .filter((p) => validCat === "all" || (p.categories || []).includes(validCat))
    .sort((a, b) => soldOut(a) - soldOut(b))
    .slice(0, 8);
  const activeCatObj = categories.find((c) => c.slug === validCat);

  return (
    <div>
      <Seo
        title="Dark Growers Genetics | Elite Cannabis Clones & Seeds"
        description="Your trusted source for clean, lab-tested cannabis breeder cuts. Shop 40+ strains of premium clones and regular seeds with free next-day shipping. Adults 21+."
        canonical="https://darkgrowers.com/"
      />
      {/* Hero */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <video
          data-testid="hero-video"
          autoPlay
          muted
          loop
          playsInline
          poster={HERO}
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <div className="max-w-2xl">
            <p className="label-caps text-emerald-400 mb-6 animate-fade-up">Elite Cannabis Genetics · Est. 2018</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-slate-50 animate-fade-up" style={{ animationDelay: "80ms" }}>
              Your trusted source for <span className="italic text-emerald-400">clean breeder cuts</span>.
            </h1>
            <p className="text-slate-300/90 text-base md:text-lg mt-7 max-w-xl leading-relaxed animate-fade-up" style={{ animationDelay: "160ms" }}>
              One-stop shop for all your clone and seed needs. Over 40 strains of premium, lab-tested
              genetics with high potency, incredible bag appeal, and strong terps.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-10 animate-fade-up" style={{ animationDelay: "240ms" }}>
              <Link
                to="/shop/clones"
                data-testid="hero-shop-clones"
                className="group flex items-center justify-center gap-2 bg-emerald-600 text-white hover:bg-emerald-500 transition-colors font-medium px-9 py-4 text-sm tracking-wide"
              >
                Browse All Clones
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/shop/seeds"
                data-testid="hero-shop-seeds"
                className="flex items-center justify-center border border-white/25 text-slate-200 hover:border-white/60 hover:text-white transition-colors px-9 py-4 text-sm tracking-wide"
              >
                Shop Seeds
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="border-y border-white/10 bg-[#050505] py-4 overflow-hidden">
        <div className="marquee-track">
          {Array.from({ length: 2 }).map((_, i) => (
            <span key={i} className="font-display text-2xl text-slate-500 px-6">
              DARKGROWERS is your One-Stop Shop for All Your Clone And Seed Needs ·&nbsp;
              <span className="text-emerald-500/60">Exotics & Candy Strains</span> ·&nbsp;
            </span>
          ))}
        </div>
      </div>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <div className="grid md:grid-cols-2 gap-6">
          {[
            { img: CLONE_IMG, label: "Clones", to: "/shop/clones", desc: "Rooted, vigorous breeder cuts ready to grow." },
            { img: SEED_IMG, label: "Seeds", to: "/shop/seeds", desc: "Regular (non-feminized) seeds — packs of 6 or 12 from the Breeder Circle." },
          ].map((c) => (
            <Link
              key={c.label}
              to={c.to}
              data-testid={`category-${c.label.toLowerCase()}`}
              className="group relative aspect-[16/10] overflow-hidden border border-white/5 bg-black"
            >
              <img src={c.img} onError={onImgError} alt={c.label} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-contain object-right p-6 group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
              <div className="absolute bottom-0 left-0 p-8">
                <h3 className="font-display text-4xl text-slate-50">{c.label}</h3>
                <p className="text-slate-300 text-sm mt-1">{c.desc}</p>
                <span className="inline-flex items-center gap-2 text-emerald-400 text-sm mt-4 group-hover:gap-3 transition-all">
                  Explore <ArrowRight size={15} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Shop by Category — interactive browser */}
      {categories.filter((c) => c.clone_count > 0).length > 0 && (
        <section className="border-y border-white/10 bg-[#080808]" data-testid="shop-by-category">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
              <div>
                <p className="label-caps text-emerald-500 mb-3">Browse the Library</p>
                <h2 className="font-display text-4xl md:text-5xl text-slate-100">Shop by Category</h2>
              </div>
              <Link to="/shop/clones" className="hidden sm:inline-flex items-center gap-2 text-slate-300 hover:text-emerald-400 text-sm whitespace-nowrap">
                All clones <ArrowRight size={15} />
              </Link>
            </div>

            <div
              className="flex gap-2.5 overflow-x-auto pb-2 -mx-1 px-1 [touch-action:pan-x] overscroll-x-contain [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
              data-testid="category-pills"
              onPointerDownCapture={(e) => console.log("[DG-PILL] row POINTERDOWN on:", e.target?.getAttribute?.("data-testid") || e.target?.tagName, "| type:", e.pointerType)}
              onTouchStartCapture={(e) => console.log("[DG-PILL] row TOUCHSTART on:", e.target?.getAttribute?.("data-testid") || e.target?.tagName)}
            >
              <button
                onClick={() => selectCat("all")}
                type="button"
                data-testid="pill-all"
                className={`shrink-0 [touch-action:manipulation] label-caps text-xs px-5 py-2.5 border transition-all ${
                  validCat === "all"
                    ? "bg-emerald-500 text-[#050505] border-emerald-500 font-semibold"
                    : "bg-transparent text-emerald-400 border-emerald-800 hover:border-emerald-500 hover:shadow-[0_0_12px_rgba(16,185,129,0.35)]"
                }`}
              >
                All <span className={validCat === "all" ? "text-black/60" : "text-slate-500"}>({clones.length})</span>
              </button>
              {categories.filter((c) => c.clone_count > 0).map((c) => (
                <button
                  key={c.slug}
                  onClick={() => selectCat(c.slug)}
                  type="button"
                  data-testid={`pill-${c.slug}`}
                  className={`shrink-0 [touch-action:manipulation] label-caps text-xs px-5 py-2.5 border transition-all ${
                    validCat === c.slug
                      ? "bg-emerald-500 text-[#050505] border-emerald-500 font-semibold"
                      : "bg-transparent text-emerald-400 border-emerald-800 hover:border-emerald-500 hover:shadow-[0_0_12px_rgba(16,185,129,0.35)]"
                  }`}
                >
                  {c.name} <span className={validCat === c.slug ? "text-black/60" : "text-slate-500"}>({c.clone_count})</span>
                </button>
              ))}
            </div>

            {filteredClones.length === 0 ? (
              <p className="text-slate-500 text-center py-16" data-testid="category-browser-empty">
                No drops in this category right now — check back Friday.
              </p>
            ) : (
              <div key={validCat} className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5 mt-8 animate-in fade-in duration-500" data-testid="category-browser-grid">
                {filteredClones.map((p, i) => (
                  <ProductCard key={p.id} product={p} index={i} />
                ))}
              </div>
            )}

            {activeCatObj && filteredClones.length > 0 && (
              <div className="mt-8 text-center">
                <Link
                  to={`/shop/clones/category/${activeCatObj.slug}`}
                  data-testid="view-all-category"
                  className="inline-flex items-center gap-2 border border-emerald-700 text-emerald-400 hover:bg-emerald-500 hover:text-[#050505] px-6 py-3 label-caps text-xs transition-colors"
                >
                  View all {activeCatObj.name} <ArrowRight size={15} />
                </Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Cuts Available This Week */}
      {drop.products.length > 0 && (
        <section className="border-y border-emerald-900/40 bg-gradient-to-b from-emerald-950/25 to-black" data-testid="home-weekly-drop">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
              <div>
                <span className="inline-flex items-center gap-2 label-caps text-emerald-400 text-xs mb-2">
                  <Sparkles size={14} /> Cuts Available This Week
                </span>
                <h2 className="font-display text-4xl md:text-5xl text-slate-100" data-testid="home-drop-theme">{drop.theme_label}</h2>
                <p className="text-slate-400 text-sm mt-2 flex items-center gap-2"><Truck size={14} className="text-emerald-500" /> Rooted now &amp; ready to ship next business day.</p>
              </div>
              <Link to="/weekly-drop" className="hidden sm:flex items-center gap-2 text-slate-300 hover:text-emerald-400 text-sm">
                See all this week's cuts <ArrowRight size={15} />
              </Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
              {drop.products.slice(0, 4).map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="label-caps text-emerald-500 mb-2">Hand-selected</p>
            <h2 className="font-display text-4xl md:text-5xl text-slate-100">Featured Genetics</h2>
          </div>
          <Link to="/shop" className="hidden sm:flex items-center gap-2 text-slate-300 hover:text-emerald-400 text-sm">
            View all <ArrowRight size={15} />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* Trust */}
      <section className="border-y border-white/10 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid md:grid-cols-3 gap-12">
          {[
            { icon: ShieldCheck, title: "Quality Genetics", desc: "Over 40 strains of premium genetics, tested and certified by reputable breeders and cultivations." },
            { icon: Sparkles, title: "Surplus of Flavors", desc: "High testers with terpenes that will have you amazed — great bag appeal and strong terps." },
            { icon: Users, title: "Community Driven", desc: "We treat others as we want to be treated. Respect is key. Grow, test, and select with us." },
          ].map((f) => (
            <div key={f.title}>
              <f.icon size={26} strokeWidth={1.5} className="text-emerald-500 mb-5" />
              <h3 className="font-display text-2xl text-slate-100 mb-3">{f.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 2026 New Seed Line — BIG NAN */}
      {bigNan.length > 0 && (
        <section className="border-y border-white/10 bg-gradient-to-b from-emerald-950/20 to-black">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
              <div>
                <p className="label-caps text-emerald-400 mb-3 flex items-center gap-2">
                  <Sparkles size={14} /> New for 2026
                </p>
                <h2 className="font-display text-5xl md:text-6xl text-slate-50 leading-[0.9]">
                  The <span className="italic text-emerald-400">Big Nan</span> Seed Line
                </h2>
                <p className="text-slate-400 text-sm mt-5 max-w-xl leading-relaxed">
                  Our freshest 2026 drop — an exclusive Breeder Circle collection of regular seeds,
                  every strain crossed with the legendary <span className="text-slate-200">Big Nan</span> male.
                  Packs of 6 or 12.
                </p>
              </div>
              <Link to="/shop/seeds" className="hidden sm:flex items-center gap-2 text-slate-300 hover:text-emerald-400 text-sm whitespace-nowrap">
                Shop all seeds <ArrowRight size={15} />
              </Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5" data-testid="big-nan-grid">
              {bigNan.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Shipping / Fresh clones */}
      <section className="border-y border-white/10 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid md:grid-cols-2 gap-12 items-center">
          <div className="relative aspect-square md:aspect-[4/5] max-h-[480px] overflow-hidden border border-white/10 bg-black">
            <img
              src="/dg-clamshell.png"
              alt="Dark Growers live clone clamshell packaging"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
              data-testid="shipping-clone-image"
            />
          </div>
          <div>
            <p className="label-caps text-emerald-500 mb-3">Free Next-Day Shipping</p>
            <h2 className="font-display text-4xl md:text-5xl text-slate-100 leading-tight">
              Live clones, <span className="italic text-emerald-400">shipped fresh</span> to your door.
            </h2>
            <p className="text-slate-400 text-sm mt-6 max-w-lg leading-relaxed">
              Every order ships free with next-day delivery in discreet, insulated clone shippers —
              rooted cuttings arrive vigorous and ready to grow.
            </p>
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 px-4 py-2.5 text-sm mt-7">
              <Tag size={15} /> Buy 2+ clones &amp; use code DARK25 for 25% off your order.
            </div>
          </div>
        </div>
      </section>

      {/* What Growers Are Saying */}
      {reviews.length > 0 && (
        <section className="border-y border-white/10 bg-[#080808]" data-testid="reviews-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
              <div>
                <p className="label-caps text-emerald-500 mb-3 flex items-center gap-2">
                  <Sparkles size={14} /> Real Feedback
                </p>
                <h2 className="font-display text-4xl md:text-5xl text-slate-100">What Growers Are Saying</h2>
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <button
                  onClick={() => scrollReviews(-1)}
                  data-testid="reviews-prev"
                  aria-label="Previous reviews"
                  className="w-11 h-11 flex items-center justify-center border border-emerald-800 text-emerald-400 hover:bg-emerald-500 hover:text-[#050505] transition-colors"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  onClick={() => scrollReviews(1)}
                  data-testid="reviews-next"
                  aria-label="Next reviews"
                  className="w-11 h-11 flex items-center justify-center border border-emerald-800 text-emerald-400 hover:bg-emerald-500 hover:text-[#050505] transition-colors"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
            <div
              ref={reviewsRef}
              data-testid="reviews-carousel"
              className="flex gap-5 overflow-x-auto pb-4 -mx-1 px-1 snap-x snap-mandatory [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden [touch-action:pan-x]"
            >
              {reviews.map((r) => (
                <figure
                  key={r.id}
                  data-testid={`review-card-${r.id}`}
                  className="snap-start shrink-0 w-[85%] sm:w-[380px] flex flex-col justify-between border border-white/10 bg-gradient-to-b from-[#0F0F0F] to-black p-8 relative"
                >
                  <Quote size={40} className="text-emerald-500/25 absolute top-6 right-6" />
                  {r.rating ? (
                    <div className="flex gap-1 mb-5 text-emerald-400" data-testid={`review-stars-${r.id}`}>
                      {Array.from({ length: r.rating }).map((_, i) => (
                        <Star key={i} size={15} fill="currentColor" strokeWidth={0} />
                      ))}
                    </div>
                  ) : null}
                  <blockquote className="text-slate-200 text-[0.95rem] leading-relaxed flex-1">
                    "{r.quote}"
                  </blockquote>
                  <figcaption className="mt-6 pt-5 border-t border-white/10">
                    <p className="text-slate-100 text-sm font-medium">{r.author_handle || "Verified Grower"}</p>
                    {r.strain && <p className="label-caps text-emerald-500/70 text-[0.6rem] mt-1">{r.strain}</p>}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Breeder Circle CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24 text-center">
        <p className="label-caps text-emerald-500 mb-4">We are a community of cannabis breeders</p>
        <h2 className="font-display text-4xl md:text-6xl text-slate-100 max-w-3xl mx-auto leading-tight">
          Looking for a home to <span className="italic text-emerald-400">grow, test, and select</span> with?
        </h2>
        <p className="text-slate-400 text-sm mt-6 max-w-xl mx-auto">
          Since 2018 we've traveled the world sourcing the best breeder cuts we can get our hands on —
          breeding the future of cannabis at the same time.
        </p>
        <Link
          to="/shop"
          data-testid="cta-shop"
          className="inline-flex items-center gap-2 bg-emerald-600 text-white hover:bg-emerald-500 transition-colors font-medium px-10 py-4 text-sm tracking-wide mt-10"
        >
          Explore the Collection <ArrowRight size={16} />
        </Link>
      </section>
    </div>
  );
}
